import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator.standalone === true;
    
    if (!isStandalone && !isIOSStandalone) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
      
      // For iOS Safari, always show the button since there's no beforeinstallprompt
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
      if (isIOS && isSafari) {
        setShowInstallButton(true);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the A2HS prompt');
      }
      setDeferredPrompt(null);
      setShowInstallButton(false);
    } else {
      // For iOS Safari or other browsers, show instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert('To install this app on your iOS device, tap the Share button and then "Add to Home Screen".');
      } else {
        alert('To install this app, look for the install option in your browser menu or address bar.');
      }
    }
  };

  if (!showInstallButton) return null;

  return (
    <>
      {/* Component-scoped delight: gentle entry when the install affordance
          appears. No bouncing or pulsing — that crosses into nag territory.
          Just announces itself with a quiet fade + lift on first mount. */}
      <style>{`
        .pwa-install-enter {
          opacity: 1;
          transform: translateY(0);
          transition:
            opacity 320ms cubic-bezier(0.23, 1, 0.32, 1),
            transform 320ms cubic-bezier(0.23, 1, 0.32, 1);
        }
        @starting-style {
          .pwa-install-enter {
            opacity: 0;
            transform: translateY(6px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .pwa-install-enter { transition-duration: 0ms; }
        }
      `}</style>
      <Button
        variant="outline"
        onClick={handleInstallClick}
        className="pwa-install-enter flex items-center space-x-2 text-gray-600 dark:text-gray-300 rounded-full"
      >
        <span>📱 Shortcut</span>
      </Button>
    </>
  );
};

export default PWAInstallButton;